/*
 * ReelsTabEntry — Phase 1C minimum
 * ================================
 *
 * Compact doorway card rendered inside the Discovery "🎬 Reels" tab.
 * Shows a 3-up preview grid of the newest public reels sorted by
 * newest-first and routes the user into the full-screen /reels
 * vertical viewer on tap. This matches the pattern used by the other
 * discovery tabs (Beats / Following / Featured) — each is a surface
 * launcher, not a competing feed.
 *
 * Explicitly OUT of scope: ranking, likes, comments, gifts, follow
 * logic, Marketplace reuse. CEO-locked to doorway + Create CTA.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export default function ReelsTabEntry() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, reels: [], err: null });

  useEffect(() => {
    let cancelled = false;
    api.get("/reels/feed", { params: { limit: 6 } })
      .then((r) => {
        if (cancelled) return;
        setState({ loading: false, reels: r.data?.reels || [], err: null });
      })
      .catch((e) => {
        if (cancelled) return;
        setState({
          loading: false, reels: [],
          err: e?.response?.data?.detail || "Couldn't load Reels",
        });
      });
    return () => { cancelled = true; };
  }, []);

  const { loading, reels, err } = state;

  return (
    <div className="space-y-5" data-testid="reels-tab-entry">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-pink-200/70 font-black">RGM Reels</div>
          <h2 className="text-xl sm:text-2xl font-black text-white">60-second drops from the community</h2>
          <p className="text-sm text-white/60 mt-1">
            Vertical, mobile-first short-form video for your music.
          </p>
        </div>
        <Button
          onClick={() => navigate("/create/reel")}
          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-black shrink-0"
          data-testid="reels-tab-create-btn"
        >+ Create</Button>
      </div>

      {loading && (
        <div className="text-sm text-white/60" data-testid="reels-tab-loading">Loading Reels…</div>
      )}
      {!loading && err && (
        <div className="text-sm text-amber-300" data-testid="reels-tab-error">{err}</div>
      )}
      {!loading && !err && reels.length === 0 && (
        <div className="rounded-xl border border-white/10 bg-black/40 p-6 text-center" data-testid="reels-tab-empty">
          <div className="text-5xl mb-2">🎬</div>
          <div className="text-white font-black">No Reels yet</div>
          <p className="text-sm text-white/60 mt-1 max-w-sm mx-auto">
            Be the first — record a freestyle or upload a video from your phone.
          </p>
          <Button
            onClick={() => navigate("/create/reel")}
            className="mt-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-black"
            data-testid="reels-tab-empty-create"
          >Create a Reel</Button>
        </div>
      )}
      {!loading && !err && reels.length > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="reels-tab-grid">
            {reels.map((r) => (
              <Link
                key={r.reel_id}
                to={`/reels?reel_id=${encodeURIComponent(r.reel_id)}`}
                className="relative aspect-[9/16] bg-black/70 border border-white/10 rounded-xl overflow-hidden hover:border-pink-400/60 transition-colors"
                data-testid={`reels-tab-item-${r.reel_id}`}
              >
                {r.poster_url
                  ? <img src={r.poster_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                  : (r.media_kind === "image"
                    ? <img src={r.media_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                    : <div className="w-full h-full flex items-center justify-center text-white/40 text-2xl">🎬</div>)}
                <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                  <div className="text-[11px] text-white/90 font-bold truncate">
                    @{r.creator?.name || "RGM Artist"}
                  </div>
                  {r.caption && (
                    <div className="text-[10px] text-white/70 line-clamp-1">{r.caption}</div>
                  )}
                </div>
              </Link>
            ))}
          </div>
          <div>
            <Button
              onClick={() => navigate("/reels")}
              variant="outline"
              className="w-full text-white border-white/20 hover:bg-white/5"
              data-testid="reels-tab-open-feed"
            >
              Open full Reels feed →
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
